import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { usePortfolios } from "../context/PortfolioContext";
import BrandLogo from "../components/BrandLogo";
import { TechWatermark } from "../components/TechWatermark";
import { copyToClipboard } from "../lib/clipboard";
import {
  Plus,
  Copy,
  Pencil,
  Ban,
  CheckCircle2,
  Trash2,
  ExternalLink,
  Search,
  Calendar,
  MoreVertical,
  Users,
  Store,
  LayoutGrid,
  CircleDot,
  FileText,
  Sparkles,
  CreditCard,
  LayoutTemplate,
  Link2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "../hooks/use-toast";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

const StatCard = ({ icon: Icon, label, value, tone }) => (
  <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
    <span className={`flex h-12 w-12 items-center justify-center rounded-xl ${tone}`}>
      <Icon className="h-6 w-6" />
    </span>
    <div>
      <p className="text-2xl font-bold text-slate-900">{value}</p>
      <p className="text-sm text-slate-500">{label}</p>
    </div>
  </div>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const {
    portfolios,
    createPortfolio,
    duplicatePortfolio,
    renamePortfolio,
    toggleStatus,
    deletePortfolio,
  } = usePortfolios();

  const [query, setQuery] = useState("");
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const filtered = useMemo(
    () =>
      portfolios.filter((p) =>
        p.name.toLowerCase().includes(query.toLowerCase())
      ),
    [portfolios, query]
  );

  const stats = useMemo(
    () => ({
      total: portfolios.length,
      active: portfolios.filter((p) => p.status === "active").length,
      suspended: portfolios.filter((p) => p.status === "suspended").length,
    }),
    [portfolios]
  );

  const handleCreate = () => setCreateOpen(true);

  const handleCreateType = (kind) => {
    const name = kind === "card" ? "Nueva tarjeta" : "Nuevo portafolio";
    const item = createPortfolio(name, kind);
    setCreateOpen(false);
    toast({
      title: kind === "card" ? "Tarjeta digital creada" : "Portafolio creado",
      description: "Empieza a editarlo ahora.",
    });
    navigate(`/editor/${item.id}`);
  };

  const copyLink = (path) => {
    const full = `${window.location.origin}${path}`;
    copyToClipboard(full);
    toast({ title: "Enlace copiado", description: full });
  };

  const handleDuplicate = (id) => {    const copy = duplicatePortfolio(id);
    if (copy) toast({ title: "Portafolio duplicado", description: copy.name });
  };

  const openRename = (p) => {
    setRenameTarget(p);
    setRenameValue(p.name);
  };

  const confirmRename = () => {
    if (renameTarget && renameValue.trim()) {
      renamePortfolio(renameTarget.id, renameValue.trim());
      toast({ title: "Nombre actualizado" });
    }
    setRenameTarget(null);
  };

  return (
    <div className="relative min-h-screen bg-[#0b1530]">
      <TechWatermark />
      {/* Top bar */}
      <header className="safe-top sticky top-0 z-30 border-b border-white/10 bg-white/95 shadow-lg shadow-black/20 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 pb-4 pt-7 sm:px-6 sm:pt-5">
          <BrandLogo className="h-12 w-auto min-w-0 max-w-[58vw] object-contain object-left sm:h-14" />
          <div className="flex shrink-0 items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/plataformas")}
              className="gap-1.5 px-2 lg:px-3"
              data-testid="dashboard-platforms-button"
            >
              <Store className="h-4 w-4" />
              <span className="hidden lg:inline">Plataformas</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/admin")}
              className="gap-1.5 px-2 lg:px-3"
              data-testid="dashboard-admin-button"
            >
              <Users className="h-4 w-4" />
              <span className="hidden lg:inline">Clientes</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/cotizaciones")}
              className="gap-1.5 px-2 lg:px-3"
            >
              <FileText className="h-4 w-4" />
              <span className="hidden lg:inline">Cotizaciones</span>
            </Button>
            <Button
              onClick={handleCreate}
              size="sm"
              className="gap-1.5 bg-blue-600 px-2 hover:bg-blue-700 lg:px-3"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden lg:inline">Nuevo portafolio</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-6xl px-6 py-8">
        <div className="mb-8">
          <h1 className="font-display text-3xl font-bold text-white">
            Portafolios
          </h1>
          <p className="mt-1 text-blue-100/70">
            Gestiona, duplica y publica portafolios web para tus clientes.
          </p>
        </div>

        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatCard icon={LayoutGrid} label="Portafolios totales" value={stats.total} tone="bg-blue-50 text-blue-600" />
          <StatCard icon={CircleDot} label="Activos" value={stats.active} tone="bg-emerald-50 text-emerald-600" />
          <StatCard icon={Ban} label="Suspendidos" value={stats.suspended} tone="bg-amber-50 text-amber-600" />
        </div>

        <div className="mb-6 flex w-full flex-col items-center gap-4 rounded-2xl border border-blue-200 bg-gradient-to-r from-[#12245a] to-[#2E6BFF] p-5 text-center text-white shadow-sm sm:flex-row sm:items-center sm:text-left">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/15">
            <Sparkles className="h-6 w-6" />
          </span>
          <button onClick={() => navigate("/muestras")} className="min-w-0 flex-1 text-center sm:text-left">
            <span className="block font-display text-lg font-bold">
              Muestras UAICARD BUSINESS
            </span>
            <span className="mt-0.5 block text-sm text-blue-100">
              Página de presentación de tarjetas digitales y portafolios web · editable
            </span>
          </button>
          <div className="flex w-full shrink-0 flex-wrap items-center justify-center gap-2 sm:w-auto sm:flex-nowrap sm:justify-end">
            <button
              onClick={() => navigate("/muestras?edit=1")}
              className="flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-2 text-xs font-semibold text-white transition hover:bg-white/25"
            >
              <Pencil className="h-4 w-4" /> Editar
            </button>
            <button
              onClick={() => copyLink("/muestras")}
              className="flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-2 text-xs font-semibold text-white transition hover:bg-white/25"
            >
              <Link2 className="h-4 w-4" /> Copiar enlace
            </button>
            <button
              onClick={() => window.open("/muestras", "_blank")}
              className="rounded-full bg-white/15 p-2 transition hover:bg-white/25"
              aria-label="Abrir"
            >
              <ExternalLink className="h-5 w-5 text-blue-100" />
            </button>
          </div>
        </div>

        <div className="mb-6 flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar portafolio..."
              className="bg-white pl-9"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((p) => (
            <div
              key={p.id}
              className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:shadow-lg"
            >
              <div className="relative h-32 bg-gradient-to-br from-[#12245a] via-[#1B3FB8] to-[#2E6BFF]">
                {(() => {
                  const isCard = p.kind === "card";
                  const media = isCard ? p.cover?.url : p.profile.avatar;
                  const isVideo = isCard
                    ? p.cover?.type === "video"
                    : p.profile.avatarType === "video";
                  if (!media) return null;
                  return isVideo ? (
                    <video
                      src={media}
                      muted
                      loop
                      autoPlay
                      playsInline
                      className="h-full w-full object-cover opacity-60"
                    />
                  ) : (
                    <img src={media} alt="" className="h-full w-full object-cover opacity-60" />
                  );
                })()}
                <div className="absolute -bottom-6 left-5">
                  <div className="h-16 w-16 overflow-hidden rounded-2xl border-4 border-white bg-white shadow-md">
                    {(() => {
                      const isCard = p.kind === "card";
                      const media = isCard ? p.cover?.url : p.profile.avatar;
                      const isVideo = isCard
                        ? p.cover?.type === "video"
                        : p.profile.avatarType === "video";
                      if (!media)
                        return (
                          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-blue-500 to-indigo-600 font-display text-xl font-bold text-white">
                            {p.profile.businessName.charAt(0)}
                          </div>
                        );
                      return isVideo ? (
                        <video src={media} muted loop autoPlay playsInline className="h-full w-full object-cover" />
                      ) : (
                        <img src={media} alt="" className="h-full w-full object-cover" />
                      );
                    })()}
                  </div>
                </div>
                <span
                  className={`absolute right-3 top-3 rounded-full px-2.5 py-1 text-xs font-semibold ${
                    p.status === "active"
                      ? "bg-emerald-500 text-white"
                      : "bg-amber-500 text-white"
                  }`}
                >
                  {p.status === "active" ? "Activo" : "Suspendido"}
                </span>
              </div>

              <div className="px-5 pb-5 pt-8">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="truncate font-display text-lg font-bold text-slate-900">
                      {p.name}
                    </h3>
                    <p className="truncate text-sm text-slate-500">
                      /{p.domain || "uaicard"}/{p.slug}
                    </p>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700">
                        <MoreVertical className="h-5 w-5" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem onClick={() => navigate(`/editor/${p.id}`)}>
                        <Pencil className="mr-2 h-4 w-4" /> Editar en vivo
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openRename(p)}>
                        <Pencil className="mr-2 h-4 w-4" /> Renombrar
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDuplicate(p.id)}>
                        <Copy className="mr-2 h-4 w-4" /> Duplicar
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => toggleStatus(p.id)}>
                        {p.status === "active" ? (
                          <>
                            <Ban className="mr-2 h-4 w-4" /> Suspender
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="mr-2 h-4 w-4" /> Reactivar
                          </>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-red-600 focus:text-red-600"
                        onClick={() => setDeleteTarget(p)}
                      >
                        <Trash2 className="mr-2 h-4 w-4" /> Eliminar
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className="mt-3 flex items-center gap-1.5 text-xs text-slate-400">
                  <Calendar className="h-3.5 w-3.5" />
                  Inicio: {formatDate(p.startDate)}
                </div>

                <div className="mt-4 flex gap-2">
                  <Button
                    className="flex-1 gap-1.5 bg-blue-600 hover:bg-blue-700"
                    onClick={() => navigate(`/editor/${p.id}`)}
                  >
                    <Pencil className="h-4 w-4" /> Editar en vivo
                  </Button>
                  <Button
                    variant="outline"
                    className="gap-1.5"
                    onClick={() => copyLink(`/api/v/${p.slug}?t=${Date.now()}`)}
                    title="Copiar enlace para compartir"
                  >
                    <Link2 className="h-4 w-4" /> Copiar
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => window.open(`/${p.domain || "uaicard"}/${p.slug}`, "_blank")}
                    title="Ver publicado"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}

          {/* New card */}
          <button
            onClick={handleCreate}
            className="flex min-h-[280px] flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-blue-300/40 bg-white/10 text-blue-100/80 transition hover:border-blue-400 hover:text-blue-500"
          >
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-blue-50 text-blue-500">
              <Plus className="h-7 w-7" />
            </span>
            <span className="font-medium">Crear nuevo portafolio</span>
          </button>
        </div>
      </main>

      {/* Rename dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>¿Qué quieres crear?</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <button
              onClick={() => handleCreateType("card")}
              className="group flex flex-col items-start gap-2 rounded-2xl border-2 border-slate-200 p-5 text-left transition hover:border-blue-500 hover:bg-blue-50"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100 text-blue-600">
                <CreditCard className="h-6 w-6" />
              </span>
              <span className="font-display text-base font-bold text-slate-900">
                Tarjeta digital
              </span>
              <span className="text-sm text-slate-500">
                Portada horizontal, contacto, redes y galería. Ideal como tarjeta
                de presentación.
              </span>
            </button>
            <button
              onClick={() => handleCreateType("portfolio")}
              className="group flex flex-col items-start gap-2 rounded-2xl border-2 border-slate-200 p-5 text-left transition hover:border-blue-500 hover:bg-blue-50"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-100 text-indigo-600">
                <LayoutTemplate className="h-6 w-6" />
              </span>
              <span className="font-display text-base font-bold text-slate-900">
                Portafolio web
              </span>
              <span className="text-sm text-slate-500">
                Página completa con secciones, carruseles, ubicación y video.
              </span>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rename dialog */}
      <Dialog open={!!renameTarget} onOpenChange={(o) => !o && setRenameTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Renombrar portafolio</DialogTitle>
          </DialogHeader>
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            placeholder="Nombre del portafolio"
            onKeyDown={(e) => e.key === "Enter" && confirmRename()}
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameTarget(null)}>
              Cancelar
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={confirmRename}>
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este portafolio?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará "{deleteTarget?.name}"
              permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                deletePortfolio(deleteTarget.id);
                toast({ title: "Portafolio eliminado" });
                setDeleteTarget(null);
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Dashboard;
